import React, {
    ReactElement,
    MouseEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
    ComponentProps,
} from 'react';
import clamp from 'lodash/clamp';
import useIntersection from 'react-use/lib/useIntersection';
import useToggle from 'react-use/lib/useToggle';
import { buildImageSource, buildImageSourceSet } from '../../../utils/buildImageSource';
import { BREAKPOINTS } from '../../../utils/styles/breakpoints';
import { useDoubleClick } from '../../../utils/useDoubleClick';
import { Carousel } from '../../carousel/Carousel';
import { CarouselIndicators } from '../../carousel/CarouselIndicators';
import { Skeleton } from '../../feedback/Skeleton';
import { IconKebab } from '../../icon/IconKebab';
import { IconButton } from '../../input/IconButton';
import { Box } from '../../layout/Box';
import { TranslateBox } from '../../layout/TranslateBox';
import { Image } from '../../media/Image';
import { MediaCover } from '../../media/MediaCover';
import { Retry } from '../../media/Retry';
import { Video } from '../../media/Video';
import { ReactionState, type PostUI } from '../types';
import { AnimateFooter, AnimateFooterSkeleton } from './AnimateFooter';
import { Root, Header, Shadow, Content } from './styles';

const makeTransition = (...styles: string[]) =>
    styles.map((style) => `${style} 200ms linear`).join(', ');

const makeMediaParams = (post: PostUI, index: number) => ({
    mediaId: post.content[index].id,
    mediaIndex: index,
});

export const calculateHeight = (width: number, slides: PostUI['content'] = []) => {
    const [firstSlide] = slides;
    const [firstMedia] = firstSlide?.media ?? [];
    const ratio = clamp((firstMedia?.width || 1) / (firstMedia?.height || 1), 9 / 16, 16 / 9);
    const height = width / ratio;
    const finalHeigth =
        window.innerWidth < BREAKPOINTS.sm ? height : Math.min(height, window.innerHeight);

    // Если ширина больше высоты, то делаем ее равной высоте, чтобы карточка была квадратной
    return Math.max(finalHeigth, width);
};

interface PostCardProps extends ComponentProps<typeof Box> {
    post: PostUI;
    index: number;
    onBookmark?: VoidFunction;
    onShare?: (params: { mediaId: string }) => void;
    onReaction: ({
        postNumber,
        reactionState,
    }: {
        postNumber: number;
        reactionState: PostUI['reactionState'];
    }) => void;
    onSubscribe?: (params: { postNumber: number }) => void;
    onClickOutClick: (params: { mediaId: string }) => void;
    onSlideChange: (params: { mediaId: string; mediaIndex: number }) => void;
    onExpand: (params: { mediaId: string; mediaIndex: number }) => void;
    onVideoMuteChange: (params: { mediaId: string; mute: boolean }) => void;
    onVideoEnd: (params: { mediaId: string }) => void;
    onVideoStart: (params: { mediaId: string }) => void;
    onVideoPlay: (params: { mediaId: string }) => void;
    onVideoPause: (params: { mediaId: string }) => void;
    onVisibilityChange: (params: {
        mediaId: string;
        isInViewport: boolean;
        postNumber: number;
    }) => void;
    renderMenu: (trigger: ReactElement) => ReactElement | null;
}

export function PostCard(props: PostCardProps) {
    const { post, renderMenu } = props;
    const [isExpanded, toggleIsExpanded] = useToggle(false);
    const postRef = useRef<null | HTMLDivElement>(null);
    const isVisibleRef = useRef(false);
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [isSoundControlContainerEnabled, setIsSoundControlContainerEnabled] = useState(false);
    const [soundContainer, setSoundContainer] = useState<null | HTMLDivElement>(null);

    const onShare = useCallback(
        () => props.onShare?.(makeMediaParams(post, activeIndex)),
        [post.content, activeIndex, props.onShare],
    );

    const onSubscribe = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            props.onSubscribe?.({ postNumber: props.index });
            event.stopPropagation();
        },
        [props.index, props.onSubscribe],
    );
    const onBookmark = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            props.onBookmark?.();
            event.stopPropagation();
        },
        [props.onBookmark],
    );

    const onReaction = useCallback(
        (reactionState: PostUI['reactionState']) => {
            props.onReaction({ postNumber: props.index, reactionState });
        },
        [props.index, props.onReaction],
    );

    const onDoubleClick = useDoubleClick(
        () => props.onReaction({ postNumber: props.index, reactionState: ReactionState.LIKE }),
        [props.index, props.onReaction],
    );

    const onClickOutClick = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            props.onClickOutClick?.(makeMediaParams(post, activeIndex));
            event.stopPropagation();
        },
        [activeIndex, post, props.onClickOutClick],
    );

    const onSlideChange = useCallback(
        (index: number) => {
            setActiveIndex(index);
            props.onSlideChange?.(makeMediaParams(post, index));
        },
        [post, props.onSlideChange],
    );

    const onExpand = useCallback(
        (event: MouseEvent<any>) => {
            event.stopPropagation();

            if (!isExpanded) {
                props.onExpand?.(makeMediaParams(post, activeIndex));
            }

            toggleIsExpanded();
        },
        [activeIndex, post, isExpanded, toggleIsExpanded, props.onExpand],
    );

    const onVideoMuteChange = useCallback(
        (mute: boolean) =>
            props.onVideoMuteChange?.({ ...makeMediaParams(post, activeIndex), mute }),
        [activeIndex, post, props.onVideoMuteChange],
    );
    const onVideoEnd = useCallback(
        () => props.onVideoEnd?.(makeMediaParams(post, activeIndex)),
        [activeIndex, post, props.onVideoEnd],
    );
    const onVideoStart = useCallback(
        () => props.onVideoStart?.(makeMediaParams(post, activeIndex)),
        [activeIndex, post, props.onVideoStart],
    );
    const onVideoPlay = useCallback(
        () => props.onVideoPlay?.(makeMediaParams(post, activeIndex)),
        [activeIndex, post, props.onVideoPlay],
    );
    const onVideoPause = useCallback(
        () => props.onVideoPause?.(makeMediaParams(post, activeIndex)),
        [activeIndex, post, props.onVideoPause],
    );

    const [firstContent] = post.content;
    const ratio = Math.min(
        (firstContent?.media[0]?.width || 1) / (firstContent?.media[0]?.height || 1),
        1,
    );

    const intersection = useIntersection(postRef, {
        root: null,
        threshold: 0.5,
    });

    useEffect(() => {
        const isVisible = Boolean(intersection && intersection.intersectionRatio >= 0.5);

        if (isVisible !== isVisibleRef.current) {
            isVisibleRef.current = isVisible;
            props.onVisibilityChange?.({
                ...makeMediaParams(post, activeIndex),
                isInViewport: isVisible,
                postNumber: props.index,
            });
        }
    }, [
        post,
        activeIndex,
        isVisibleRef.current,
        intersection?.intersectionRatio,
        props.onVisibilityChange,
    ]);

    useEffect(() => {
        const currentClide = post.content[activeIndex];
        setIsSoundControlContainerEnabled(currentClide.type !== 'image');
    }, [activeIndex, post]);

    return (
        <Root ref={postRef}>
            <Header background="linear-gradient(180deg, rgba(0, 0, 0, 0.30) 0%, rgba(0, 0, 0, 0.00) 100%)">
                {isSoundControlContainerEnabled && (
                    <TranslateBox
                        transition={makeTransition('transform')}
                        ref={(element) => {
                            setSoundContainer(element);
                        }}
                        zIndex={5}
                        trigger={isExpanded}
                        x1={44}
                        x2={4}
                    />
                )}
                {renderMenu(
                    <Box transition={makeTransition('opacity')} opacity={isExpanded ? 1 : 0}>
                        <IconButton size="s" cursor={isExpanded ? 'pointer' : 'default'}>
                            <IconKebab color="#FFFFFF" />
                        </IconButton>
                    </Box>,
                )}
                {/* Заглушка, чтобы кебаб с opacity 0 не кликался */}
                {!isExpanded && <Box position="absolute" width="28px" height="28px" />}
            </Header>
            <Shadow
                onClick={onExpand}
                opacity={isExpanded ? 1 : 0}
                pointerEvents={isExpanded ? 'auto' : 'none'}
            />
            <Carousel
                count={post.content.length}
                activeIndex={activeIndex}
                onSlideChange={onSlideChange}
                renderItem={(index) => {
                    const slide = post.content[index];

                    return (
                        <MediaCover
                            key={index}
                            ratio={ratio}
                            minWidth="100%"
                            onClick={onDoubleClick}
                        >
                            {slide.type === 'image' && (
                                <Retry>
                                    <Image
                                        width="100%"
                                        height="100%"
                                        src={buildImageSource(slide.media, 'card')}
                                        srcSet={buildImageSourceSet(slide.media, 'card')}
                                    />
                                </Retry>
                            )}
                            {slide.type === 'video' && (
                                <Video
                                    media={slide.media[0]}
                                    onVideoMuteChange={onVideoMuteChange}
                                    onVideoEnd={onVideoEnd}
                                    onVideoStart={onVideoStart}
                                    onVideoPlay={onVideoPlay}
                                    onVideoPause={onVideoPause}
                                    soundContainer={
                                        index === activeIndex ? soundContainer : undefined
                                    }
                                />
                            )}
                        </MediaCover>
                    );
                }}
            />
            <Box position="absolute" top={0} left={0} width="100%" zIndex={5} px={1.5} py={3 / 4}>
                <CarouselIndicators
                    variant="story"
                    activeIndex={activeIndex}
                    count={post.content.length}
                />
            </Box>
            <Content
                pointerEvents="none"
                background="linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.30) 70%)"
            >
                <AnimateFooter
                    post={post}
                    postElement={postRef.current}
                    isExpanded={isExpanded}
                    onSubscribe={onSubscribe}
                    onReaction={onReaction}
                    onShare={onShare}
                    onBookmark={onBookmark}
                    onClickOutClick={onClickOutClick}
                    onExpand={onExpand}
                />
            </Content>
        </Root>
    );
}

export const PostCardSkeleton = () => (
    <Root>
        <Skeleton width="100%" variant="rect" ratio={1}>
            <Content width="100%" height="100%" justifyContent="flex-end" direction="column">
                <AnimateFooterSkeleton />
            </Content>
        </Skeleton>
    </Root>
);
